/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/core";
import { Box } from "@material-ui/core";
import { useEmoBalance } from "hooks/paw/useEmoBalance";
import React from "react";
import { useSelector } from "store";

import { StreamingActionHeader } from "./StreamingActionHeader";
import { StreamingAnnouncement } from "./StreamingAnnouncement";
import StreamingCommentV2 from "./StreamingCommentV2";
import { StreamingLiveEventV2 } from "./StreamingLiveEventV2";
import { StreamingSelectGiftV2 } from "./StreamingSelectGiftV2";
import { StreamingSendCommentV2 } from "./StreamingSendCommentV2";
import { useSendComment } from "./useSendComment";

import type { ActionAreaType } from "./useActionAreaType";
import type { Timestamp } from "@google-cloud/firestore";
import type { THEOPlayer } from "@spwn/types/external";
import type { CommentFilterType } from "modules/streaming";
import type { FC } from "react";
import type { Store } from "store";
import type { EventInfo } from "utility";

type Props = {
  player: THEOPlayer;
  eventStart: Timestamp;
  eventInfo: EventInfo;
  mainVideoId: string;
  THEOPlayerElementHeight: number;
  isOpenAnnouncement: boolean;
  hasDvrStartTime: boolean;
  isChatAdmin: boolean;
  handleToggleAnnouncement: () => void;
  isEditingAnnouncement: boolean;
  setIsEditingAnnouncement: (isEditing: boolean) => void;
  streamingAnnouncement: string;
  setStreamingAnnouncement: (announcement: string) => void;
  onStreamingAnnouncementLocalDeleted: () => void;
  actionAreaType: ActionAreaType;
  selectedCommentFilter: CommentFilterType;
  handleChangeCommentFilter: (filter: CommentFilterType) => void;
  handleCloseActionArea: () => void;
  isLiveEventEnabled: boolean;
  isOpenLiveEvent: boolean;
  handleToggleLiveEvent: () => void;
  isPollingEnabled: boolean;
};

/**
 * UserActionArea
 * comment, gift, (later: goods sales, quiz, vote)
 * @param props
 */
const StreamingBottomAreaCommentForSP: React.FC<Props> = (props) => {
  // ----- Hooks -----
  const { paidValue: emoBalance } = useEmoBalance();
  const {
    commentInputRef,
    iconUrls,
    comment,
    giftItems,
    selectedGiftItem,
    canSendComment,
    canPurchaseGiftItem,
    isOpenGiftDrawer,
    handleSetComment,
    handleSendComment,
    handleOpenGiftDrawer,
    handleCloseGiftDrawer,
    handleClickSelectedGift,
    handleOpenPawChargeModal,
  } = useSendComment({
    mainVideoId: props.mainVideoId,
  });

  // ----- Redux State -----
  const isDarkModeEnabled = useSelector(
    (state) => state.streaming.streamingSettings.isDarkModeEnabled
  );
  const user = useSelector((state: Store) => state.auth.user);
  const streamingType = useSelector((state) => state.streaming.streamingType);

  return (
    <StreamingActionAreaLayout
      THEOPlayerElementHeight={props.THEOPlayerElementHeight}
      header={
        <StreamingActionHeader
          isDarkModeEnabled={isDarkModeEnabled}
          selectedCommentFilter={props.selectedCommentFilter}
          handleChangeCommentFilter={props.handleChangeCommentFilter}
          handleToggleAnnouncement={props.handleToggleAnnouncement}
          handleCloseActionArea={props.handleCloseActionArea}
          showCommentFilter={true}
          showAnnouncement={
            props.isChatAdmin || props.streamingAnnouncement !== ""
          }
          isLiveEventEnabled={props.isLiveEventEnabled}
          handleToggleLiveEvent={props.handleToggleLiveEvent}
        />
      }
    >
      {props.isOpenLiveEvent && <StreamingLiveEventV2 />}

      <StreamingAnnouncement
        eventId={props.eventInfo.eventId}
        mainVideoId={props.mainVideoId}
        streamingType={streamingType}
        isOpenAnnouncement={props.isOpenAnnouncement}
        isEditingAnnouncement={props.isEditingAnnouncement}
        setIsEditingAnnouncement={props.setIsEditingAnnouncement}
        streamingAnnouncement={props.streamingAnnouncement}
        setStreamingAnnouncement={props.setStreamingAnnouncement}
        onStreamingAnnouncementLocalDeleted={
          props.onStreamingAnnouncementLocalDeleted
        }
      />

      {streamingType === "Live" && user.uid && (
        <StreamingSendCommentV2
          isDarkModeEnabled={isDarkModeEnabled}
          isOpenGiftDrawer={isOpenGiftDrawer}
          isSelectedGift={selectedGiftItem !== null}
          canPurchaseGiftItem={canPurchaseGiftItem}
          userIcon={iconUrls?.["32"]}
          userName={user.displayName ?? ""}
          inputRef={commentInputRef}
          comment={comment}
          canSendComment={canSendComment}
          giftItems={giftItems}
          onClickSendComment={handleSendComment}
          setComment={handleSetComment}
          onClickOpenGiftDrawer={handleOpenGiftDrawer}
          onClickOpenPawChargeModal={handleOpenPawChargeModal}
        />
      )}

      {isOpenGiftDrawer && giftItems.length > 0 && (
        <StreamingSelectGiftV2
          isDarkModeEnabled={isDarkModeEnabled}
          canPurchaseGiftItem={canPurchaseGiftItem}
          iconUrls={iconUrls?.["32"]}
          userName={user.displayName ?? ""}
          comment={comment}
          emoBalance={emoBalance}
          giftItems={giftItems}
          selectedGiftItem={selectedGiftItem}
          canSendComment={canSendComment}
          onClickSendComment={handleSendComment}
          onClickCloseGiftDrawer={handleCloseGiftDrawer}
          onClickSelectedGift={handleClickSelectedGift}
          onClickOpenPawChargeModal={handleOpenPawChargeModal}
        />
      )}

      <Box flex={"1 1 auto"}>
        <StreamingCommentV2
          player={props.player}
          eventStart={props.eventStart}
          streamingType={streamingType}
          eventInfo={props.eventInfo}
          mainVideoId={props.mainVideoId}
          selectedCommentFilter={props.selectedCommentFilter}
          THEOPlayerElementHeight={props.THEOPlayerElementHeight}
          hasDvrStartTime={props.hasDvrStartTime}
          isPollingEnabled={props.isPollingEnabled}
        />
      </Box>
    </StreamingActionAreaLayout>
  );
};
export default StreamingBottomAreaCommentForSP;

const StreamingActionAreaLayout: FC<{
  children: React.ReactNode;
  header: React.ReactNode;
  THEOPlayerElementHeight: number;
}> = ({ children, header, THEOPlayerElementHeight }) => {
  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      gridGap={"16px"}
      padding={"16px"}
      minHeight={`${THEOPlayerElementHeight}px`}
      css={css`
        height: calc(100vh - ${THEOPlayerElementHeight}px); /* fallback */
        @supports (height: 100dvh) {
          height: calc(100dvh - ${THEOPlayerElementHeight}px);
        }
      `}
    >
      {header}
      {children}
    </Box>
  );
};
